import { useAuth, useSignIn } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { toast } from '@/hooks/ui/use-toast';
import { getFallbackURL } from '@/lib/route';
import logger from '@/services/logger.service';

export const VerifyToken = () => {
  const hasCalled = useRef(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { isLoaded, isSignedIn, signOut } = useAuth();
  const {
    signIn,
    isLoaded: isLoadedSignIn,
    setActive: setActiveSignIn,
  } = useSignIn();

  if (!isLoaded || !isLoadedSignIn) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  // If there is no token, show error and redirect to sign-in
  if (!token) {
    toast({
      variant: 'destructive',
      title: 'Error',
      description: 'No token found. Please sign in again.',
    });
    return <Navigate to={getFallbackURL()} replace />;
  }

  const handleVerifyToken = async (): Promise<void> => {
    // If user is already signed in (common after SSO callback), sign out first
    if (isSignedIn) {
      try {
        await signOut?.();
      } catch (err) {
        logger.error('Failed to sign out before joining org', err as Error);
      }
    }

    const activeOrgId = window.localStorage.getItem('active-org-id');
    if (!activeOrgId) {
      console.error('No active organization ID found');
      return;
    }

    try {
      if (hasCalled.current) {
        return;
      }
      hasCalled.current = true;

      const signInAttempt = await signIn.create({
        strategy: 'ticket',
        ticket: token ?? '',
      });

      if (signInAttempt.status === 'complete') {
        await setActiveSignIn?.({
          session: signInAttempt.createdSessionId,
          redirectUrl: `${window.location.origin}/dashboard`,
        });
      } else {
        logger.error('Organization sign-in attempt failed', undefined, {
          signInAttempt,
          token,
        });
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to join the organization',
        });
      }
    } catch (err) {
      logger.error('Organization sign-in failed', err as Error, { token });
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to join the organization',
      });
    }
  };

  handleVerifyToken();

  return (
    <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <div className="mb-4 flex items-center justify-center">
          <Loader2 className="size-4 animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">Verifying Token</h2>
        <p className="mt-2 text-sm text-gray-600">
          Please wait while we verify the token...
        </p>
      </div>
    </div>
  );
};
